// src/components/home/HeroDemo.js
// Démo animée du hero : trois scènes jouées en boucle, 100 % CSS + état React.
// Seuls transform / opacity sont animés ; tout se met en pause hors écran,
// onglet caché ou en prefers-reduced-motion (on montre alors l'état final).
import React, { useEffect, useRef, useState } from 'react';
import {
  FileBadge,
  Scale,
  ListOrdered,
  UploadCloud,
  FileText,
  CheckCircle2,
  Trophy,
} from 'lucide-react';
import styles from './HeroDemo.module.css';
import { useAnimationGate, useSceneLoop, rankRows, scoreTone } from './useDemoAnimation';

/* ═══════════════════════════════════════════════════════════
   SCÉNARIO — durée (ms) de chaque étape, par scène
   ═══════════════════════════════════════════════════════════ */

const SCENES = [
  { key: 'generator', label: 'Générateur', Icon: FileBadge,   durations: [600, 1000, 1600, 1500, 2600] },
  { key: 'matching',  label: 'Matching',   Icon: Scale,       durations: [800, 1600, 1200, 3000] },
  { key: 'ranking',   label: 'Classement', Icon: ListOrdered, durations: [700, 800, 800, 800, 800, 2800] },
];

const SCENE_DURATIONS = SCENES.map((s) => s.durations);
const sum = (values) => values.reduce((a, b) => a + b, 0);

const cx = (...classes) => classes.filter(Boolean).join(' ');

/* ═══════════════════════════════════════════════════════════
   SCÈNE 1 — Générateur : CV déposé → dossier PowerPoint
   ═══════════════════════════════════════════════════════════ */

const GeneratorScene = ({ step }) => (
  <div className={styles.scene}>
    <div className={cx(styles.drop, step >= 1 && styles.dropFilled)}>
      <span className={styles.dropHint}>
        <UploadCloud size={16} strokeWidth={2} />
        Glissez un CV ici
      </span>
      <span className={styles.dropFile}>
        <FileText size={15} strokeWidth={2} />
        CV_Consultant.pdf
        <small>PDF · 3 pages</small>
      </span>
    </div>

    <div className={cx(styles.item, styles.process, step >= 2 && styles.on)}>
      <div className={styles.processTop}>
        <span>{step >= 3 ? 'Structuration terminée' : 'Extraction · Anonymisation…'}</span>
        <strong>{step >= 3 ? '100 %' : ''}</strong>
      </div>
      <div className={styles.track}>
        <span className={cx(styles.fill, styles.fillSlow)} />
      </div>
    </div>

    <div className={cx(styles.slide, step >= 3 && styles.on)}>
      <div className={cx(styles.item, styles.slideBand)} style={{ '--i': 0 }}>
        <span>Dossier de compétences</span>
        <span className={styles.slideLogo} />
      </div>
      <div className={styles.slideBody}>
        <div className={cx(styles.item, styles.slideId)} style={{ '--i': 1 }}>
          <span className={styles.slideAvatar}>CD</span>
          <div>
            <strong>Consultant Data</strong>
            <small>8 ans d’expérience · Nantes</small>
          </div>
        </div>
        <div className={styles.slideLines}>
          {['92%', '78%', '86%'].map((w, i) => (
            <span key={w} className={cx(styles.item, styles.line)} style={{ '--i': 2 + i, '--lw': w }} />
          ))}
        </div>
        <div className={styles.chips}>
          {['Python', 'Azure', 'Spark', 'dbt'].map((chip, i) => (
            <span key={chip} className={cx(styles.item, styles.chip)} style={{ '--i': 5 + i }}>
              {chip}
            </span>
          ))}
        </div>
      </div>
    </div>

    <span className={cx(styles.item, styles.ready, step >= 4 && styles.on)}>
      <CheckCircle2 size={14} strokeWidth={2.5} />
      Dossier prêt · PPTX éditable
    </span>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   SCÈNE 2 — Matching CV / offre
   ═══════════════════════════════════════════════════════════ */

const SCORE = 87;
const GAUGE_RADIUS = 42;
const GAUGE_CIRC = 2 * Math.PI * GAUGE_RADIUS;
const COUNT_TICKS = 18;

const PILLARS = [
  { label: 'Compétences techniques', value: 23, max: 25 },
  { label: 'Expertise sectorielle',  value: 17, max: 20 },
  { label: 'Certifications',         value: 11, max: 15 },
  { label: 'Stabilité',              value: 9,  max: 10 },
];

const MatchingScene = ({ step, running }) => {
  // Compteur 0 → 87 : ~18 mises à jour du texte, uniquement pendant l'étape 1
  const [tick, setTick] = useState(0);
  const counting = step === 1 && running;

  useEffect(() => {
    if (!counting) return undefined;
    setTick(0);
    const id = setInterval(() => {
      setTick((t) => {
        if (t + 1 >= COUNT_TICKS) clearInterval(id);
        return Math.min(t + 1, COUNT_TICKS);
      });
    }, 1400 / COUNT_TICKS);
    return () => clearInterval(id);
  }, [counting]);

  let shown = SCORE;
  if (step === 0) shown = 0;
  else if (counting) {
    const p = tick / COUNT_TICKS;
    shown = Math.round(SCORE * (1 - (1 - p) ** 3));
  }

  return (
    <div className={styles.scene}>
      <div className={cx(styles.item, styles.vs, styles.on)}>
        <span className={styles.file}>CV_Consultant.pdf</span>
        <span className={styles.vsPill}>VS</span>
        <span className={styles.file}>Mission_Data_Eng.pdf</span>
      </div>

      <div className={styles.scoreRow}>
        <div className={styles.gauge}>
          <svg viewBox="0 0 100 100" width="104" height="104">
            <circle className={styles.gaugeTrack} r={GAUGE_RADIUS} cx="50" cy="50" strokeWidth="7" />
            <circle
              className={styles.gaugeArc}
              r={GAUGE_RADIUS}
              cx="50"
              cy="50"
              strokeWidth="7"
              style={{
                strokeDasharray: GAUGE_CIRC,
                strokeDashoffset: step >= 1 ? GAUGE_CIRC * (1 - SCORE / 100) : GAUGE_CIRC,
              }}
            />
          </svg>
          <div className={styles.gaugeCenter}>
            <strong>{shown}%</strong>
            <small>Score RH</small>
          </div>
        </div>

        <div className={cx(styles.verdictCol, step >= 3 && styles.on)}>
          <span className={cx(styles.item, styles.verdict)} style={{ '--i': 0 }}>
            <CheckCircle2 size={13} strokeWidth={2.5} />
            GO
          </span>
          <p className={cx(styles.item, styles.summary)} style={{ '--i': 1 }}>
            Profil aligné sur les compétences critiques. Deux points à
            confirmer en entretien.
          </p>
        </div>
      </div>

      <div className={cx(styles.pillars, step >= 2 && styles.on)}>
        {PILLARS.map(({ label, value, max }, i) => (
          <div key={label} className={styles.pillar} style={{ '--i': i }}>
            <div className={styles.pillarTop}>
              <span>{label}</span>
              <strong>{step >= 2 ? `${value}/${max}` : `–/${max}`}</strong>
            </div>
            <div className={styles.track}>
              <span className={styles.fill} style={{ '--s': value / max }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   SCÈNE 3 — Classement live d'une pile de CV
   ═══════════════════════════════════════════════════════════ */

// Ordre de dépôt ; les scores tombent dans cet ordre et la liste se reclasse
const RANK_ROWS = [
  { name: 'Consultant_A.pdf', score: 64 },
  { name: 'Consultant_B.pdf', score: 91 },
  { name: 'Consultant_C.pdf', score: 42 },
  { name: 'Consultant_D.pdf', score: 78 },
];

const RankingScene = ({ step }) => {
  const revealed = Math.min(step, RANK_ROWS.length);
  const ranks = rankRows(RANK_ROWS, revealed);
  const done = revealed === RANK_ROWS.length;

  return (
    <div className={styles.scene}>
      <div className={styles.rankHead}>
        <div>
          <p className={styles.rankTitle}>Classement des candidats</p>
          <p className={styles.rankSub}>Data Engineer — Nantes</p>
        </div>
        <span className={cx(styles.rankChip, done && styles.rankChipDone)}>
          {done ? 'Terminé' : `Analyse ${revealed}/${RANK_ROWS.length}`}
        </span>
      </div>

      <div className={styles.rankList} style={{ '--rows': RANK_ROWS.length }}>
        {RANK_ROWS.map((row, i) => {
          const scored = i < revealed;
          const rank = ranks[i];
          const top = scored && rank === 0;
          const tone = scoreTone(row.score);
          return (
            <div
              key={row.name}
              className={cx(styles.rankRow, scored && styles.on, top && styles.rankRowTop)}
              style={{ '--rank': rank, '--i': i }}
            >
              <span className={styles.rankBadge}>
                {top ? <Trophy size={13} strokeWidth={2} /> : scored ? `#${rank + 1}` : '–'}
              </span>
              <span className={styles.rankName}>{row.name}</span>
              <span className={styles.rankTrack}>
                <span className={cx(styles.fill, styles[`tone_${tone}`])} style={{ '--s': row.score / 100 }} />
              </span>
              {scored
                ? <span className={cx(styles.rankScore, styles[`toneText_${tone}`])}>{row.score} %</span>
                : <span className={styles.rankPending}>…</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PLAYER
   ═══════════════════════════════════════════════════════════ */

const HeroDemo = () => {
  const rootRef = useRef(null);
  const { running, reduced } = useAnimationGate(rootRef);
  const { scene, step, run, round, goTo } = useSceneLoop(SCENE_DURATIONS, running);

  // En reduced-motion : pas de lecture, on affiche la scène choisie terminée
  const shownStep = reduced ? SCENES[scene].durations.length - 1 : step;
  const { key: sceneKey } = SCENES[scene];

  return (
    <div
      ref={rootRef}
      className={cx(styles.demo, !running && styles.paused, reduced && styles.reduced)}
      role="group"
      aria-label="Démonstration animée de CVtoSkill"
    >
      <div className={styles.chrome}>
        <span className={styles.dots} aria-hidden="true">
          <span className={styles.dot} />
          <span className={styles.dot} />
          <span className={styles.dot} />
        </span>
        <div className={styles.tabs}>
          {SCENES.map(({ key, label, Icon, durations }, i) => {
            const active = i === scene;
            return (
              <button
                key={key}
                type="button"
                className={cx(styles.tab, active && styles.tabActive)}
                onClick={() => goTo(i)}
                aria-pressed={active}
              >
                <Icon size={13} strokeWidth={2.25} aria-hidden="true" />
                <span>{label}</span>
                {active && !reduced && (
                  <span
                    key={`${run}:${round}`}
                    className={styles.tabProgress}
                    style={{ '--d': `${sum(durations)}ms` }}
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.stage} aria-hidden="true">
        {/* La clé remonte la scène à chaque passage pour rejouer son entrée */}
        <React.Fragment key={`${sceneKey}:${run}:${round}`}>
          {sceneKey === 'generator' && <GeneratorScene step={shownStep} />}
          {sceneKey === 'matching'  && <MatchingScene step={shownStep} running={running} />}
          {sceneKey === 'ranking'   && <RankingScene step={shownStep} />}
        </React.Fragment>
      </div>
    </div>
  );
};

export default HeroDemo;
